/**
 * Authentication Service
 * Handles user authentication, registration, and session management
 */

import { AppAuthData, PersonRecord, User, UserAppData } from '../types/data';
import { storageService } from './storage.service';
import { APP_CONFIG } from '../config/app.config';
import { createPersonRecordFromUser } from '../utils/personRecord';
import { API_CONFIG } from '../config/app.config';
import axios from 'axios';
import { hashPin } from '../utils';
import { logService } from './log.service';

class AuthService {
  private apiClient = axios.create({
    baseURL: API_CONFIG.BASE_URL,
    timeout: API_CONFIG.TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
    },
    // Disable caching at axios level
    adapter: undefined, // Use default adapter but with no-cache
  });

  constructor() {
    // Add request interceptor to include auth token
    this.apiClient.interceptors.request.use(
      async (config) => {
        // Skip token lookup for authentication endpoints (login/register)
        // This avoids race conditions with storage initialization on fresh app loads
        const isAuthEndpoint = config.url?.includes('/auth/login') || 
                              config.url?.includes('/auth/register');
        
        if (!isAuthEndpoint) {
          const token = await storageService.getAuthToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * Login with credentials
   * Includes retry logic and cache busting for new builds
   * Handles DNS cache and network issues on fresh Android builds
   */
  async login(username: string, password: string): Promise<AppAuthData> {
    // Add cache buster to prevent DNS/HTTP caching issues on fresh builds
    const cacheBuster = Date.now();
    
    let lastError: any;
    const maxRetries = 2; // Try up to 3 times total
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Log attempt for debugging
        if (attempt > 0) {
          console.log(`Login attempt ${attempt + 1}/${maxRetries + 1} after ${attempt * 2}s delay`);
        }
        
        const response = await this.apiClient.post(
          API_CONFIG.ENDPOINTS.LOGIN,
          {
            email: username, // Laravel controller expects 'email' field
            password,
          },
          {
            // Add cache buster as query param - helps with DNS cache
            params: { _t: cacheBuster + attempt },
            // Override headers for this specific request
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
            },
            // Increase timeout for fresh builds (DNS resolution might be slow)
            timeout: 45000, // 45 seconds (vs default 30)
          }
        );

        // If we get here, request succeeded - break out of retry loop
        
        const existingAuthData = (await storageService.getAuthData()) as AppAuthData | null;

        const normalizedUser = this.normalizeUser(response.data.user);

        const authData: AppAuthData = {
          user: normalizedUser,
          token: response.data.token,
          expires_at: response.data.expires_at,
          authenticated: true,
        };

        // Preserve device PIN settings for the same user
        if (
          existingAuthData &&
          this.getStableUserId(existingAuthData.user) === this.getStableUserId(authData.user)
        ) {
          authData.pinEnabled = existingAuthData.pinEnabled;
          authData.pinHash = existingAuthData.pinHash;
        }

        // Save auth data
        await storageService.setAuthData(authData);
        await storageService.setAuthToken(authData.token);
        await storageService.setSessionUnlocked(true);

        // Store last login user ID for PIN authentication
        const userId = this.getUserId(authData.user);
        if (userId) {
          await storageService.setLastLoginUserId(userId);
        }

        // Reset offline mode flag when user logs in with credentials
        await storageService.updateSettings({
          offlineMode: false,
          isFirstLaunch: false,
          hasCompletedOnboarding: true,
        });

        // IMPORTANT: Wait for PersonRecord to be created/fetched BEFORE returning
        // This prevents race conditions where UI tries to access person data before it exists
        await this.initializeUserData(authData.user);

        // Refresh user data from server to ensure latest channel_details (including currency_symbol)
        await this.refreshUserFromServer();

        return authData;
        
      } catch (error: any) {
        lastError = error;
        
        // Check if this is a retryable error
        const isNetworkError = error.code === 'ERR_NETWORK' || 
                              error.code === 'ECONNABORTED' ||
                              error.message?.includes('Network Error') ||
                              error.message?.includes('timeout') ||
                              !error.response; // No response = network issue
        
        const isServerError = error.response?.status >= 500;
        
        // Only retry on network errors or server errors (not auth failures)
        const shouldRetry = (isNetworkError || isServerError) && attempt < maxRetries;
        
        if (shouldRetry) {
          // Wait before retrying: 2s, then 4s
          const delay = (attempt + 1) * 2000;
          console.log(`Network error, retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue; // Try again
        } else {
          // Don't retry - break out and throw error
          break;
        }
      }
    }
    
    // If we get here, all retries failed - throw the last error with enhanced message
    const error = lastError;
    let errorMessage = 'Login failed';
    
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      errorMessage = 'Connection timeout. The server took too long to respond. Please check your internet connection and try again.';
    } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
      errorMessage = 'Network error. Unable to reach the server. Please check your internet connection and try again.';
    } else if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const serverMessage = error.response.data?.message;
      
      if (status === 401) {
        errorMessage = serverMessage || 'Invalid username or password. Please check your credentials and try again.';
      } else if (status === 422) {
        // Validation error
        const errors = error.response.data?.errors;
        if (errors) {
          const errorMessages = Object.values(errors).flat();
          errorMessage = serverMessage || errorMessages[0] || 'Validation error. Please check your input.';
        } else {
          errorMessage = serverMessage || 'Invalid input. Please check your credentials.';
        }
      } else if (status === 429) {
        errorMessage = 'Too many login attempts. Please wait a few minutes and try again.';
      } else if (status >= 500) {
        errorMessage = `Server error (${status}). Please try again later or contact support.`;
      } else {
        errorMessage = serverMessage || `Login failed with status ${status}. Please try again.`;
      }
    } else if (error.request) {
      // Request made but no response received
      errorMessage = 'No response from server. Please check your internet connection and try again.';
    } else {
      // Something else happened
      errorMessage = error.message || 'An unexpected error occurred. Please try again.';
    }
    
    // Attach original error for debugging
    const enhancedError = new Error(errorMessage);
    (enhancedError as any).originalError = error;
    throw enhancedError;
  }

  /**
   * Register new user
   */
  async register(data: {
    name: string;
    email: string;
    username: string;
    password: string;
    channel: string;
  }): Promise<AppAuthData> {
    try {
      // Laravel expects password_confirmation for validation
      const response = await this.apiClient.post(API_CONFIG.ENDPOINTS.REGISTER, {
        ...data,
        password_confirmation: data.password,
      });

      const existingAuthData = (await storageService.getAuthData()) as AppAuthData | null;

      const normalizedUser = this.normalizeUser(response.data.user);

      const authData: AppAuthData = {
        user: normalizedUser,
        token: response.data.token,
        expires_at: response.data.expires_at,
        authenticated: true,
      };

      // Preserve device PIN settings for the same user
      if (
        existingAuthData &&
        this.getStableUserId(existingAuthData.user) === this.getStableUserId(authData.user)
      ) {
        authData.pinEnabled = existingAuthData.pinEnabled;
        authData.pinHash = existingAuthData.pinHash;
      }

      // Save auth data
      // IMPORTANT: Initialize user data (fetch/create PersonRecord) BEFORE saving auth data
      // This ensures PersonRecord exists before any UI components try to access it
      await this.initializeUserData(authData.user);

      await storageService.setAuthData(authData);
      await storageService.setAuthToken(authData.token);
      await storageService.setSessionUnlocked(true);

      // Store last login user ID for PIN authentication
      const userId = this.getUserId(authData.user);
      if (userId) {
        await storageService.setLastLoginUserId(userId);
      }

      // Reset offline mode flag when user registers
      await storageService.updateSettings({
        offlineMode: false,
        isFirstLaunch: false,
        hasCompletedOnboarding: true,
      });

      // Refresh user data from server to ensure latest channel_details (including currency_symbol)
      await this.refreshUserFromServer();

      return authData;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  }

  /**
   * Logout
   */
  async logout(): Promise<void> {
    try {
      const token = await storageService.getAuthToken();
      if (token) {
        // Call logout endpoint
        await this.apiClient.post(
          API_CONFIG.ENDPOINTS.LOGOUT,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Clear local data
      await storageService.clearAuthData();
      await storageService.removeAuthToken();
      await storageService.setSessionUnlocked(false);
    }
  }

  /**
   * Lock the app for this browser session.
   * Keeps the stored token + PIN so the next screen can be PIN-first.
   */
  async lock(): Promise<void> {
    await storageService.setSessionUnlocked(false);
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const authData = await storageService.getAuthData();
    if (!authData) return false;

    // Migrate stored user id to UUID when server provides it.
    try {
      const userAny = authData.user as any;
      if (userAny?.uuid && authData.user?.id && String(authData.user.id) !== String(userAny.uuid)) {
        await storageService.setAuthData({
          ...authData,
          user: this.normalizeUser(authData.user),
        });
      }
    } catch {
      // ignore
    }

    // Current schema: explicit authenticated flag
    if (authData.authenticated) return true;

    // Legacy schema migration: treat existing user+token as authenticated
    const token = (await storageService.getAuthToken()) ?? authData.token;
    const hasLegacySession = !!authData.user?.id && !!token;
    if (!hasLegacySession) return false;

    // Best-effort: repair persisted shape so app startup/PIN gate works consistently
    try {
      if (!(await storageService.getAuthToken()) && token) {
        await storageService.setAuthToken(token);
      }
      await storageService.setAuthData({
        ...authData,
        token,
        authenticated: true,
      });
    } catch {
      // ignore
    }

    return true;
  }

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<User | null> {
    const authData = await storageService.getAuthData();
    return authData?.user ? this.normalizeUser(authData.user) : null;
  }

  /**
   * Refresh user data from server
   * Call this to update user details including channel_details with currency_symbol
   */
  async refreshUserFromServer(): Promise<User | null> {
    try {
      const token = await storageService.getAuthToken();
      if (!token) return null;

      const response = await this.apiClient.get(API_CONFIG.ENDPOINTS.PROFILE);
      const userData = response.data?.user;
      
      if (userData) {
        // Update stored auth data with fresh user info
        const authData = await storageService.getAuthData();
        if (authData) {
          const updatedAuthData = {
            ...authData,
            user: this.normalizeUser(userData),
          };
          await storageService.setAuthData(updatedAuthData);
          return updatedAuthData.user;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Failed to refresh user from server:', error);
      return null;
    }
  }

  private getStableUserId(user: User | null | undefined): string {
    if (!user) return '';
    const userAny = user as any;
    return (typeof userAny.uuid === 'string' && userAny.uuid.length > 0)
      ? userAny.uuid
      : String(user.id);
  }

  private normalizeUser(raw: any): User {
    if (!raw) return raw;

    const uuid = typeof raw.uuid === 'string' ? raw.uuid : undefined;
    const dbId = raw.id;

    // Prefer UUID for app-level identity and routing.
    const id = uuid ? String(uuid) : String(dbId);

    return {
      ...raw,
      id,
      uuid,
      db_id: uuid ? dbId : raw.db_id,
    } as User;
  }

  private async tryFetchPersonRecordFromServer(personId: string): Promise<PersonRecord | null> {
    try {
      const endpoint = API_CONFIG.ENDPOINTS.PERSON_DETAIL.replace(':id', encodeURIComponent(personId));
      await logService.info('Auth', `Fetching PersonRecord from server: ${endpoint} for personId: ${personId}`);
      const response = await this.apiClient.get(endpoint);
      const person = response.data?.person as PersonRecord | undefined;
      if (!person || !person.id) {
        await logService.info('Auth', `No PersonRecord found on server for personId: ${personId}`);
        return null;
      }

      await logService.info('Auth', 'Successfully fetched PersonRecord from server', {
        id: person.id,
        name: person.name,
        recordCount: person.records?.length || 0,
        surveyor_id: person.surveyor_id,
      });

      // Ensure basic invariants for local usage.
      if (!(person as any).version) (person as any).version = 1;
      if (!Array.isArray((person as any).records)) (person as any).records = [];
      if (!(person as any).address) {
        (person as any).address = { district: '', upazila: '', union: '', village: '', gps_coordinates: '' };
      }
      if (!(person as any).updated_at) (person as any).updated_at = new Date().toISOString();
      // Treat freshly-fetched records as synced.
      if (!(person as any).synced_at) (person as any).synced_at = (person as any).updated_at;

      return person;
    } catch (error: any) {
      await logService.error('Auth', `Failed to fetch PersonRecord for ${personId}`, {
        status: error?.response?.status,
        message: error?.response?.data?.message || error?.message,
      });
      return null;
    }
  }

  /**
   * Validate the stored token with the server.
   * Returns false only when the server explicitly rejects the token (401/403).
   * Network/server errors are treated as "unknown" and return true to avoid locking users out.
   */
  async validateTokenWithServer(): Promise<boolean> {
    const token = await storageService.getAuthToken();
    if (!token) return false;

    try {
      await this.apiClient.get(API_CONFIG.ENDPOINTS.PROFILE);
      return true;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        return false;
      }
      return true;
    }
  }

  /**
   * Setup PIN for quick login
   */
  async setupPin(pin: string): Promise<void> {
    const currentUser = await this.getCurrentUser();
    const userId = this.getUserId(currentUser);
    const hashedPin = hashPin(pin);
    await storageService.setPinHash(hashedPin, userId);
  }

  /**
   * Validate PIN
   * Uses current user ID if authenticated, otherwise falls back to last login user ID
   */
  async validatePin(pin: string): Promise<boolean> {
    const currentUser = await this.getCurrentUser();
    let userId = this.getUserId(currentUser);
    
    // If no current user, try to get last logged in user ID
    if (!userId) {
      userId = await storageService.getLastLoginUserId();
    }
    
    const storedHash = await storageService.getPinHash(userId);
    if (!storedHash) return false;

    const hashedPin = hashPin(pin);
    return hashedPin === storedHash;
  }

  /**
   * Check if PIN is enabled
   * Uses current user ID if authenticated, otherwise falls back to last login user ID
   */
  async isPinEnabled(): Promise<boolean> {
    const currentUser = await this.getCurrentUser();
    let userId = this.getUserId(currentUser);
    
    // If no current user, try to get last logged in user ID
    if (!userId) {
      userId = await storageService.getLastLoginUserId();
    }
    
    return await storageService.isPinEnabled(userId);
  }

  /**
   * Disable PIN
   */
  async disablePin(): Promise<void> {
    const currentUser = await this.getCurrentUser();
    const userId = this.getUserId(currentUser);
    await storageService.clearPin(userId);
  }

  /**
   * Get stable user ID (uuid or id)
   */
  private getUserId(user: User | null): string | undefined {
    if (!user) return undefined;
    const userAny = user as any;
    return (typeof userAny.uuid === 'string' && userAny.uuid.length > 0)
      ? userAny.uuid
      : (user.id ? String(user.id) : undefined);
  }

  /**
   * Update locally stored authenticated user fields.
   * This keeps UI in sync when profile edits happen offline.
   */
  async updateLocalUser(patch: Partial<User>): Promise<void> {
    const authData = await storageService.getAuthData();
    if (!authData?.user) return;

    const nextAuthData: AppAuthData = {
      ...authData,
      user: {
        ...authData.user,
        ...patch,
      },
    };

    await storageService.setAuthData(nextAuthData);
  }

  /**
   * Update user profile on the server
   * Syncs user data with backend and updates local storage
   */
  async updateUserProfile(patch: Partial<User>): Promise<User> {
    try {
      const response = await this.apiClient.put(API_CONFIG.ENDPOINTS.UPDATE_PROFILE, patch);
      
      const updatedUser = this.normalizeUser(response.data.user || response.data);
      
      // Update local auth data with server response
      await this.updateLocalUser(updatedUser);
      
      return updatedUser;
    } catch (error: any) {
      // If network fails, at least update locally
      console.error('Failed to update user profile on server:', error);
      await this.updateLocalUser(patch);
      throw new Error(error.response?.data?.message || 'Failed to update profile');
    }
  }

  /**
   * Continue without login (offline mode)
   * Creates a demo user account for offline-only usage
   */
  async continueOffline(): Promise<void> {
    // Create offline demo user
    const demoUser: User = {
      id: 'demo-user-offline',
      uuid: 'demo-user-offline',
      name: 'Offline User',
      email: 'offline@local',
      username: 'offline',
      avatar: undefined,
      channel: 'offline',
    };

    // Create auth data for offline mode
    const authData: AppAuthData = {
      user: demoUser,
      token: 'offline-token',
      expires_at: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year from now
      authenticated: true,
    };

    // IMPORTANT: Initialize user data (create PersonRecord) BEFORE saving auth data
    // This prevents race condition where UI renders before PersonRecord exists
    await this.initializeUserData(demoUser);

    // Save auth data
    await storageService.setAuthData(authData);
    await storageService.setAuthToken(authData.token);
    await storageService.setSessionUnlocked(true);

    // Store last login user ID for PIN authentication
    await storageService.setLastLoginUserId(demoUser.id);

    // Update settings to enable offline mode
    await storageService.updateSettings({
      offlineMode: true,
      offlineModeWarningShown: true,
    });
  }

  /**
   * Ensure the signed-in user has a corresponding PersonRecord in local UserAppData.
   * In User Mode, DayRecords belong to a PersonRecord, so the user must exist in `persons`.
   * 
   * @param user - Optional user to ensure record for (defaults to current user)
   * @param skipServerFetch - If true, skip server fetch when local record exists (faster for PIN login)
   */
  async ensureUserPersonRecord(user?: User, skipServerFetch = false): Promise<void> {
    const resolvedUser = user ?? (await this.getCurrentUser());
    if (!resolvedUser?.id) {
      console.log('[Auth] No user to ensure PersonRecord for');
      return;
    }

    const userAny = resolvedUser as any;
    const personId = (typeof userAny.uuid === 'string' && userAny.uuid.length > 0)
      ? userAny.uuid
      : String(resolvedUser.id);

    const surveyorId = personId;

    await logService.info('Auth', 'Ensuring PersonRecord for user', {
      userId: resolvedUser.id,
      personId,
      surveyorId,
      skipServerFetch,
    });

    const existingData = (await storageService.getUserData()) as UserAppData | null;
    if (!existingData) {
      // No data yet; create it.
      await logService.info('Auth', 'No existing UserAppData, creating initial data');
      const now = new Date().toISOString();
      const settings = await storageService.getSettings();

      // Prefer server truth if it exists (fresh install, existing server record)
      const fetched = await this.tryFetchPersonRecordFromServer(personId);

      let userPersonRecord: PersonRecord;
      if (fetched) {
        userPersonRecord = fetched;
      } else {
        await logService.info('Auth', 'Creating new PersonRecord locally (not found on server)');
        const p = createPersonRecordFromUser({ user: resolvedUser, surveyorId, nowIso: now });
        // Mark as needing sync (expired/unknown server state)
        (p as any).synced_at = '1970-01-01T00:00:00.000Z';
        userPersonRecord = p;
      }

      const initialData: UserAppData = {
        app_version: APP_CONFIG.VERSION,
        lang: settings.language,
        mode: APP_CONFIG.DEFAULT_MODE,
        weight_unit: APP_CONFIG.DEFAULT_WEIGHT_UNIT,
        persons: [userPersonRecord],
        updated_at: now,
        pending_sync: false,
      };

      await storageService.setUserData(initialData);
      await logService.info('Auth', 'Initial UserAppData created with PersonRecord');
      return;
    }

    const persons = Array.isArray((existingData as any).persons) ? (existingData as any).persons : [];
    const existingIndex = persons.findIndex((p: any) => p?.id === personId);

    await logService.info('Auth', `Existing data found with ${persons.length} persons, user PersonRecord exists: ${existingIndex !== -1}`);

    let changed = false;

    if (existingIndex === -1) {
      // Only fetch from server if explicitly requested or if we don't have local data
      const fetched = skipServerFetch ? null : await this.tryFetchPersonRecordFromServer(personId);
      if (fetched) {
        await logService.info('Auth', 'Fetched user PersonRecord from server, adding to local data');
        persons.push(fetched);
      } else {
        await logService.info('Auth', 'Creating new user PersonRecord locally');
        const now = new Date().toISOString();
        const p = createPersonRecordFromUser({ user: resolvedUser, surveyorId, nowIso: now });
        (p as any).synced_at = '1970-01-01T00:00:00.000Z';
        persons.push(p);
      }
      changed = true;
    } else {
      const existingPerson = persons[existingIndex];
      if (existingPerson && existingPerson.surveyor_id !== surveyorId) {
        await logService.info('Auth', 'Correcting surveyor_id on user PersonRecord');
        existingPerson.surveyor_id = surveyorId;
        changed = true;
      }
      if (existingPerson && !existingPerson.name && resolvedUser.name) {
        await logService.info('Auth', 'Adding missing name to user PersonRecord');
        existingPerson.name = resolvedUser.name;
        changed = true;
      }
      // if (existingPerson && !existingPerson.mobile && resolvedUser.email) {
      //   existingPerson.mobile = resolvedUser.email;
      //   changed = true;
      // }
      if (existingPerson && !existingPerson.version) {
        existingPerson.version = 1;
        changed = true;
      }
      if (existingPerson && !Array.isArray(existingPerson.records)) {
        existingPerson.records = [];
        changed = true;
      }
    }

    if (!Array.isArray((existingData as any).persons)) {
      (existingData as any).persons = persons;
      changed = true;
    }

    if (changed) {
      const updatedData: UserAppData = {
        ...existingData,
        persons,
        updated_at: new Date().toISOString(),
      };
      await storageService.setUserData(updatedData);
      await logService.info('Auth', 'UserAppData updated with PersonRecord changes');
    } else {
      await logService.info('Auth', 'No changes needed to user PersonRecord');
    }
  }

  /**
   * Initialize user data after login/registration
   * Creates the initial app data structure with user's PersonRecord
   */
  private async initializeUserData(user: User): Promise<void> {
    await this.ensureUserPersonRecord(user);
  }

  /**
   * Check if a user exists on the server
   * Used for identifying orphaned accounts
   */
  async checkUserExists(userId: string): Promise<boolean> {
    try {
      // Try to fetch user profile from server
      const response = await this.apiClient.get(`/api/profile/${userId}`);
      return response.status === 200 && !!response.data;
    } catch (error: any) {
      // If 404 or any error, user doesn't exist on server
      return false;
    }
  }

  /**
   * Delete user account
   * Requires password confirmation
   */
  async deleteAccount(password: string): Promise<void> {
    try {
      // Send delete request with password confirmation
      await this.apiClient.delete(API_CONFIG.ENDPOINTS.DELETE_ACCOUNT, {
        data: {
          password,
        },
      });

      // Clear all local data after successful deletion
      await this.logout();
    } catch (error: any) {
      await logService.error('Auth', 'Account deletion failed', error);
      
      // Check if it's a wrong password error
      if (error.response?.status === 401 || error.response?.status === 403) {
        throw new Error('WRONG_PASSWORD');
      }
      
      throw error;
    }
  }
}

export const authService = new AuthService();
export default AuthService;
